var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import buttonStyles from '@cof/fs-button-core/lib/button.lit.min.js';
import { FsFocusScope } from '@cof/fs-focus-scope-wc';
import iconStyles from '@cof/fs-icon-core/lib/icon.lit.min.js';
import { fs24Close } from '@cof/fs-icons';
import { fsModalDialogDefaults, fsModalDialogMobileLayout } from '@cof/fs-modal-dialog-core';
import modalDialogStyles from '@cof/fs-modal-dialog-core/lib/modal-dialog.lit.min.js';
import { ModalDialogEvent, modalDialogEventType } from '@cof/fs-modal-dialog-core/src/modal-dialog-event.js';
import { ModalDialogManager } from '@cof/fs-modal-dialog-core/src/modal-dialog-manager.js';
import { FsTooltip } from '@cof/fs-tooltip-wc';
import { privateFsDefineComponent } from '@cof/safe-custom-element-define';
import { css, html, LitElement } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
// Define dependency components.
privateFsDefineComponent.define(FsFocusScope);
privateFsDefineComponent.define(FsTooltip);
/**
 * A ModalDialog is a window that appears on top of the current page and requires the user to take an action before
 * returning to the main page.
 *
 * ## Installation
 * ### Install the node module:
 * ```bash
 * npm i @cof/fs-modal-dialog-wc
 * ```
 *
 * ## Usage
 * To import the side-effectful registration of `fs-modal-dialog` call
 *
 * ```javascript
 * import '@cof/fs-modal-dialog-wc/fs-modal-dialog.js';
 * ```
 *
 * To import the `FsModalDialog` class as either a base class or to self-register the custom element, use
 *
 * ```javascript
 * import { FsModalDialog } from '@cof/fs-modal-dialog-wc';
 * ```
 *
 * ### Basic usage
 * The simplest way to use the ModalDialog is to slot the button that should be used to launch the ModalDialog in the
 * default slot. This will automatically handle adding the needed event listeners.
 *
 * ### Advanced usage
 * The ModalDialog allows some escape hatches in cases where the implementer needs trigger the launching of the
 * ModalDialog with a button that cannot be slotted into the ModalDialog for any number of reasons. In this case, the
 * user can leverage the public `openModalDialog` and `closeModalDialog` methods to programatically manage the Modal
 * Dialog.
 *
 * ### General usage notes
 * #### Footer action buttons
 * Footer action buttons are _intentionally uncontrolled_. The implementer is responsible for the result of any user
 * interaction with the footer actions. This also applies to closing the ModalDialog. If you need to close the
 * ModalDialog in response to user interaction, we suggest calling the public `closeModalDialog` method.
 *
 *
 * ## Anatomy
 * ### DOM structure
 * ```html
 * <fs-modal-dialog>
 *   <button class="fs-button fs-button--primary"><!-- OPEN BUTTON --></button>
 *   <h2 slot="modal-dialog-header"><!-- HEADER CONTENT --></h2>
 *   <p slot="modal-dialog-body"><!-- BODY CONTENT --></p>
 *   <fs-modal-dialog-footer>
 *     <!-- FOOTER CONTENT -->
 *   </fs-modal-dialog-footer>
 * </fs-modal-dialog>
 * ```
 *
 * @element fs-modal-dialog
 * @cssprop --fs-modal-dialog--max-height - sets the maximum height of the FsModalDialog window
 * @cssprop --fs-modal-dialog--max-width - sets the maximum width of the FsModalDialog window
 * @slot - the default slot transcludes the "trigger", the control that will open the FsModalDialog window
 * @slot modal-dialog-header - the header content of the FsModalDialog
 * @slot modal-dialog-body - the body content of the FsModalDialog
 * @slot modal-dialog-footer - the footer content of the FsModalDialog
 * @fires modal-dialog-close a cancellable event indicating that the Modal Dialog will be closed
 * @fires modal-dialog-open a cancellable event indicating that the Modal Dialog will be opened
 */
export class FsModalDialog extends LitElement {
    constructor() {
        super();
        this.accessibleTitle = fsModalDialogDefaults.accessibleTitle;
        this.dismissible = fsModalDialogDefaults.dismissible;
        this.mobileLayout = fsModalDialogDefaults.mobileLayout;
        this.open = fsModalDialogDefaults.open;
        this.scrollable = fsModalDialogDefaults.scrollable;
        this.size = fsModalDialogDefaults.size;
        this._bodyHasSlottedContent = false;
        this._footerHasSlottedContent = false;
        /**
         * Whether to forgo processing changes to the open attribute on the next cycle.
         *
         * This is needed to support update the state of the open attribute internally without causing the manager to set the
         * closed state twice.
         */
        this._forgoNextOpenAttributeUpdatedCycle = false;
        /**
         * Republish any ModalDialog events published from the ModalDialogManager.
         * Events published from an EventTarget cannot naturally bubble.
         *
         * @param event the ModalDialog event to republish
         */
        this._handleModalDialogManagerEvent = (event) => {
            if (event.type === modalDialogEventType['modal-dialog-close']) {
                this.open = false;
                this._forgoNextOpenAttributeUpdatedCycle = true;
            }
            if (event.type === modalDialogEventType['modal-dialog-open']) {
                this.open = true;
                this._forgoNextOpenAttributeUpdatedCycle = true;
            }
            const eventToRepublish = new ModalDialogEvent(event.type);
            this.dispatchEvent(eventToRepublish);
        };
        // Memoize the body element for use in the ModalDialogManager.
        if (typeof document !== 'undefined') {
            this._bodyElement = document.body;
        }
    }
    /**
     * Initialize manager.
     */
    firstUpdated() {
        this._modalDialogManager = new ModalDialogManager({
            closeButtonElement: this._closeButtonElement,
            closeButtonTooltipElement: this._closeButtonTooltipElement,
            focusScopeElement: this._focusScopeElement,
            overlayElement: this._overlayElement,
            rootElement: this._rootElement,
            windowHeaderElement: this._windowHeaderElement,
            windowBodyElement: this._windowBodyElement,
            windowFooterElement: this._windowFooterElement
        }, this._bodyElement, this._triggerElement);
        this._modalDialogManager.addEventListener(modalDialogEventType['modal-dialog-open'], this._handleModalDialogManagerEvent);
        this._modalDialogManager.addEventListener(modalDialogEventType['modal-dialog-close'], this._handleModalDialogManagerEvent);
    }
    /**
     * Reconnect manager.
     */
    connectedCallback() {
        super.connectedCallback();
        if (this._modalDialogManager != null && !this._modalDialogManager.isConnected) {
            this._modalDialogManager.connect();
            this._modalDialogManager.addEventListener(modalDialogEventType['modal-dialog-open'], this._handleModalDialogManagerEvent);
            this._modalDialogManager.addEventListener(modalDialogEventType['modal-dialog-close'], this._handleModalDialogManagerEvent);
        }
    }
    /**
     * Disconnect manager.
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._modalDialogManager != null && this._modalDialogManager.isConnected) {
            this._modalDialogManager.disconnect();
            this._modalDialogManager.removeEventListener(modalDialogEventType['modal-dialog-open'], this._handleModalDialogManagerEvent);
            this._modalDialogManager.removeEventListener(modalDialogEventType['modal-dialog-close'], this._handleModalDialogManagerEvent);
        }
    }
    /**
     * Handle property updates.
     *
     * @param changedProperties updated properties
     */
    updated(changedProperties) {
        if (changedProperties.has('open') && !this._forgoNextOpenAttributeUpdatedCycle) {
            if (this.open) {
                this._modalDialogManager.open(
                /* Only dispatch an event if this is a change rather than an initialization */
                changedProperties.get('open') != null);
            }
            else {
                this._modalDialogManager.close(
                /* Only dispatch an event if this is a change rather than an initialization */
                changedProperties.get('open') != null);
            }
        }
        this._forgoNextOpenAttributeUpdatedCycle = false;
        if (changedProperties.has('dismissible') && this.dismissible && this._closeButtonElement != null) {
            this._modalDialogManager.updateCloseButtonElement(this._closeButtonElement);
        }
    }
    /**
     * Close the ModalDialog window.
     * This is an escape hatch if the implementer determines they need to externalize the "trigger" that should launch
     * the Modal Dialog.
     */
    closeModalDialog() {
        this.open = false;
    }
    /**
     * Open the ModalDialog window.
     * This is an escape hatch if the implementer determines they need to externalize the "trigger" that should launch
     * the Modal Dialog.
     */
    openModalDialog() {
        this.open = true;
    }
    /**
     * Render the component.
     */
    render() {
        return html `
    <slot @slotchange=${this._handleDefaultSlotChange}></slot>
    <private-fs-focus-scope state="${this.open ? 'trap' : 'inert'}">
      <div
        class="
          fs-modal-dialog
          fs-modal-dialog--${this.size}
          ${this.dismissible ? 'fs-modal-dialog--dismissible' : ''}
          ${this.mobileLayout === fsModalDialogMobileLayout.fullscreen ? 'fs-modal-dialog--fullscreen-mobile' : ''}
          ${this.open ? 'fs-modal-dialog--open' : ''}
          ${this.scrollable ? 'fs-modal-dialog--scrollable' : ''}
        "
        id="root"
        role="dialog"
        aria-hidden="${!this.open}"
        aria-label="${this._getAriaLabel()}"
        aria-modal="${this.open}"
      >
        <div class="fs-modal-dialog__overlay" id="overlay"></div>
        <div class="fs-modal-dialog__window" id="window">
          <header class="fs-modal-dialog__header" id="window-header">
            <slot name="modal-dialog-header" @slotchange=${this._handleHeaderSlotChange}></slot>
            ${this.dismissible
            ? html `
                  <private-fs-tooltip position="bottom" tooltip-content="Close" trigger-aria-attribute="aria-labelledby">
                    <button class="fs-modal-dialog__close-button" id="close" type="button">
                      <svg aria-hidden="true" class="fs-icon fs-icon--primary">${unsafeSVG(fs24Close[1])}</svg>
                    </button>
                  </private-fs-tooltip>
                `
            : ''}
          </header>
          <div
            class="fs-modal-dialog__body ${this._bodyHasSlottedContent ? '' : 'fs-modal-dialog__body--empty'}"
            id="window-body"
            tabindex="${this.scrollable ? '0' : '-1'}"
          >
            <div class="fs-modal-dialog__body-scroll-tracker fs-modal-dialog__body-scroll-tracker--top"></div>
            <slot name="modal-dialog-body" @slotchange=${this._handleBodySlotChange}></slot>
            <div class="fs-modal-dialog__body-scroll-tracker fs-modal-dialog__body-scroll-tracker--bottom"></div>
          </div>
          <footer id="window-footer" class="${this._footerHasSlottedContent ? '' : 'fs-modal-dialog__footer--empty'}">
            <slot name="modal-dialog-footer" @slotchange=${this._handleFooterSlotChange}></slot>
          </footer>
        </div>
      </div>
    </private-fs-focus-scope>
  `;
    }
    /**
     * Get a descriptive AriaLabel value for the ModalDialog.
     *
     * @returns a descriptive AriaLabel value for the ModalDialog.
     */
    _getAriaLabel() {
        if (this.accessibleTitle != null && this.accessibleTitle !== '') {
            return this.accessibleTitle;
        }
        return this._derivedAccessibleTitle;
    }
    /**
     * Handle changes to the default slot.
     * This is used to make sure the "trigger" element that opens the ModalDialog when clicked is kept up to date.
     */
    _handleDefaultSlotChange() {
        const children = this._defaultSlotElement.assignedElements();
        /* c8 ignore next 4 */
        /* Failsafe - not practically testable. */
        if (children.length === 0) {
            return;
        }
        const firstChildElement = children.find((childElement) => childElement instanceof HTMLElement);
        /* c8 ignore next 4 */
        /* Failsafe - not practically testable. */
        if (firstChildElement == null) {
            return;
        }
        this._triggerElement = firstChildElement;
        if (this._modalDialogManager != null) {
            this._modalDialogManager.updateTriggerElement(this._triggerElement);
        }
    }
    /**
     * Handle changes to the header slot.
     * This is used to derive a title for the ModalDialog.
     */
    _handleHeaderSlotChange() {
        // Don't attempt to override an override from the implementer.
        const children = this._headerSlotElement.assignedElements();
        /* c8 ignore next 4 */
        /* Failsafe - not practically testable. */
        if (children.length === 0) {
            return;
        }
        this._derivedAccessibleTitle = children[0].textContent;
    }
    _handleBodySlotChange() {
        this._bodyHasSlottedContent = this._bodySlotElement.assignedElements().length > 0;
    }
    _handleFooterSlotChange() {
        this._footerHasSlottedContent = this._footerSlotElement.assignedElements().length > 0;
    }
}
FsModalDialog.canonicalName = 'modal-dialog';
FsModalDialog.styles = [
    buttonStyles,
    iconStyles,
    modalDialogStyles,
    css `
      :host {
        display: block;
      }
    `
];
__decorate([
    property({ type: String, attribute: 'accessible-title' })
], FsModalDialog.prototype, "accessibleTitle", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsModalDialog.prototype, "dismissible", void 0);
__decorate([
    property({ type: String, attribute: 'mobile-layout' })
], FsModalDialog.prototype, "mobileLayout", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsModalDialog.prototype, "open", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsModalDialog.prototype, "scrollable", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsModalDialog.prototype, "size", void 0);
__decorate([
    state()
], FsModalDialog.prototype, "_bodyHasSlottedContent", void 0);
__decorate([
    state()
], FsModalDialog.prototype, "_footerHasSlottedContent", void 0);
__decorate([
    state()
], FsModalDialog.prototype, "_derivedAccessibleTitle", void 0);
__decorate([
    query('#close')
], FsModalDialog.prototype, "_closeButtonElement", void 0);
__decorate([
    query('private-fs-tooltip')
], FsModalDialog.prototype, "_closeButtonTooltipElement", void 0);
__decorate([
    query('slot:not([name])')
], FsModalDialog.prototype, "_defaultSlotElement", void 0);
__decorate([
    query('slot[name="modal-dialog-header"]')
], FsModalDialog.prototype, "_headerSlotElement", void 0);
__decorate([
    query('slot[name="modal-dialog-body"]')
], FsModalDialog.prototype, "_bodySlotElement", void 0);
__decorate([
    query('slot[name="modal-dialog-footer"]')
], FsModalDialog.prototype, "_footerSlotElement", void 0);
__decorate([
    query('private-fs-focus-scope')
], FsModalDialog.prototype, "_focusScopeElement", void 0);
__decorate([
    query('#overlay')
], FsModalDialog.prototype, "_overlayElement", void 0);
__decorate([
    query('#root')
], FsModalDialog.prototype, "_rootElement", void 0);
__decorate([
    query('#window-header')
], FsModalDialog.prototype, "_windowHeaderElement", void 0);
__decorate([
    query('#window-body')
], FsModalDialog.prototype, "_windowBodyElement", void 0);
__decorate([
    query('#window-footer')
], FsModalDialog.prototype, "_windowFooterElement", void 0);
//# sourceMappingURL=ModalDialog.js.map